import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { BillingService } from "../../services/BillingService";
import { usePermissions } from "../../PermissionsProvider";
import { Alert, AlertDescription } from "../ui/alert";
import { CheckCircleIcon, AlertCircleIcon } from "lucide-react";
import { PaymentMethod } from "../../model_gen/billing";
import { Skeleton } from "../ui/skeleton";

interface AddCreditsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddCreditsModal: React.FC<AddCreditsModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { apiKey } = usePermissions();
  const [amount, setAmount] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] =
    useState<boolean>(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);
  const [showCustomAmount, setShowCustomAmount] = useState<boolean>(false);

  // Preset amounts in credits
  const presetAmounts = [5000, 10000, 25000, 50000, 100000];

  useEffect(() => {
    if (open && apiKey) {
      fetchPaymentMethods();
    }
  }, [open, apiKey]);

  const fetchPaymentMethods = () => {
    setLoadingPaymentMethods(true);

    BillingService.getPaymentMethods(null, apiKey).subscribe(
      (result: PaymentMethod[]) => {
        setPaymentMethods(result);
        setLoadingPaymentMethods(false);

        // Default to the default payment method
        const defaultMethod = result.find((pm) => pm.is_default);
        if (defaultMethod) {
          setSelectedPaymentMethodId(defaultMethod.id || null);
        } else if (result.length > 0) {
          setSelectedPaymentMethodId(result[0].id || null);
        }
      },
      (error: any) => {
        console.error("Error fetching payment methods:", error);
        setLoadingPaymentMethods(false);
      }
    );
  };

  const creditsToDollars = (credits: number): number => {
    return credits * 0.002;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numCredits = parseFloat(amount);

    if (isNaN(numCredits) || numCredits <= 0) {
      setError("Please enter a valid amount greater than 0 credits");
      return;
    }

    if (numCredits < 500) {
      setError("Minimum amount is 500 credits");
      return;
    }

    const numDollars = creditsToDollars(numCredits);

    if (numDollars > 10000) {
      setError(
        "Maximum amount is 5,000,000 credits ($10,000). Please contact support for larger amounts."
      );
      return;
    }

    if (!selectedPaymentMethodId) {
      setError("Please select a payment method");
      return;
    }

    setLoading(true);
    setError(null);

    BillingService.addCredits(
      numDollars,
      "usd",
      selectedPaymentMethodId,
      null,
      apiKey
    ).subscribe(
      () => {
        setSuccess(true);
        setLoading(false);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      },
      (error: any) => {
        console.error("Error adding credits:", error);
        setError(error.message || "Failed to add credits. Please try again.");
        setLoading(false);
      }
    );
  };

  const handleClose = () => {
    setAmount("");
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handlePresetClick = (presetAmount: number) => {
    setAmount(presetAmount.toString());
    setError(null);
  };

  const dollarsToCredits = (dollars: number): number => {
    // 1 credit = $0.002, so credits = dollars / 0.002
    return Math.round(dollars / 0.002);
  };

  const formatCredits = (credits: number): string => {
    return credits.toLocaleString();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Credits</DialogTitle>
          <DialogDescription>
            Purchase credits by charging your payment method.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8">
            <Alert className="border-maitai-green bg-maitai-green/10">
              <CheckCircleIcon className="h-4 w-4 text-maitai-green" />
              <AlertDescription className="text-maitai-green">
                Credits added successfully! Your balance will update shortly.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 py-4">
              {/* Preset Amounts */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Quick Select</Label>
                <div className="grid grid-cols-3 gap-2">
                  {presetAmounts.map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      variant={
                        amount === preset.toString() && !showCustomAmount
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => {
                        setAmount(preset.toString());
                        setShowCustomAmount(false);
                        setError(null);
                      }}
                      className="w-full flex flex-col items-center py-3 h-auto">
                      <span className="font-semibold">
                        {formatCredits(preset)}
                      </span>
                      <span className="text-xs opacity-70">
                        ${creditsToDollars(preset).toFixed(0)}
                      </span>
                    </Button>
                  ))}
                  {/* Other Button */}
                  <Button
                    type="button"
                    variant={showCustomAmount ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setShowCustomAmount(true);
                      setAmount("");
                      setError(null);
                    }}
                    className="flex flex-col items-center py-3 h-auto">
                    <span className="font-semibold">Other</span>
                    <span className="text-xs opacity-70">Custom</span>
                  </Button>
                </div>

                {/* Custom Amount Input - shown when "Other" is selected */}
                {showCustomAmount && (
                  <div className="space-y-1" style={{ marginTop: 12 }}>
                    <Input
                      id="amount"
                      type="number"
                      step="100"
                      min="500"
                      placeholder="Enter credits"
                      value={amount}
                      onChange={(e) => {
                        const val = e.target.value;
                        setAmount(val);
                        const num = parseFloat(val);
                        if (val && num > 0 && num < 500) {
                          setError("Minimum amount is 500 credits");
                        } else {
                          setError(null);
                        }
                      }}
                      disabled={loading}
                      autoFocus
                    />
                    {amount &&
                      parseFloat(amount) > 0 &&
                      parseFloat(amount) < 500 && (
                        <p className="text-xs text-destructive">
                          Minimum amount is 500 credits
                        </p>
                      )}
                    {amount && parseFloat(amount) >= 500 && (
                      <p className="text-xs text-muted-foreground">
                        ${creditsToDollars(parseFloat(amount)).toFixed(2)} will
                        be charged to your card
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Payment Method Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Payment Method</Label>
                {loadingPaymentMethods ? (
                  <Skeleton className="h-10 w-full" />
                ) : paymentMethods.length > 0 ? (
                  <select
                    value={selectedPaymentMethodId || ""}
                    onChange={(e) =>
                      setSelectedPaymentMethodId(parseInt(e.target.value))
                    }
                    disabled={loading}
                    className="w-full h-10 px-3 text-sm rounded-md border border-border bg-background">
                    {paymentMethods.map((pm) => (
                      <option key={pm.id} value={pm.id || ""}>
                        {pm.meta?.card?.brand} •••• {pm.meta?.card?.last4}
                        {pm.is_default ? " (Default)" : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Alert>
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertDescription>
                      No payment methods found. Please add a payment method
                      first.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircleIcon className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  loading ||
                  !amount ||
                  !selectedPaymentMethodId ||
                  paymentMethods.length === 0
                }>
                {loading ? "Adding..." : "Add Credits"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddCreditsModal;
